import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo } from 'react';

import { useFeatureFlag } from '@bonusly/who-am-i';

import { useLocalStorage } from '../../lib/storage/hooks/use-local-storage';

interface SeasonalThemeContextValue {
	/** True if the user has the feature flag enabled (can see and use seasonal themes) */
	hasSeasonalThemePermission: boolean;
	/** True if user has permission AND has toggled the theme on */
	isSeasonalThemeEnabled: boolean;
	toggleTheme: () => void;
}

export const SeasonalThemeContext = createContext<SeasonalThemeContextValue | undefined>(undefined);

export const SeasonalThemeProvider: FC<PropsWithChildren> = ({ children }) => {
	const hasFeatureFlag = useFeatureFlag('seasonal-themes');
	const [userPreference, setUserPreference] = useLocalStorage<boolean>('bonusly:seasonal-theme:fall-2025', false);

	const toggleTheme = useCallback(() => {
		setUserPreference(!userPreference);
	}, [userPreference, setUserPreference]);

	const contextValue: SeasonalThemeContextValue = useMemo(
		() => ({
			// User has permission if feature flag is enabled
			hasSeasonalThemePermission: Boolean(hasFeatureFlag),
			// Theme is enabled only if user has permission AND has chosen to enable it
			isSeasonalThemeEnabled: Boolean(hasFeatureFlag && userPreference),
			toggleTheme,
		}),
		[hasFeatureFlag, userPreference, toggleTheme]
	);

	return <SeasonalThemeContext.Provider value={contextValue}>{children}</SeasonalThemeContext.Provider>;
};

/**
 * Hook to access seasonal theme state and controls
 *
 * Returns safe defaults when used outside of SeasonalThemeProvider:
 * - hasSeasonalThemePermission: false
 * - isSeasonalThemeEnabled: false
 * - toggleTheme: no-op function
 *
 * This allows components to gracefully degrade in contexts without the provider.
 */
export const useSeasonalTheme = () => {
	const context = useContext(SeasonalThemeContext);

	// Gracefully handle usage outside of provider
	if (!context) {
		return {
			hasSeasonalThemePermission: false,
			isSeasonalThemeEnabled: false,
			toggleTheme: () => {},
		};
	}

	return context;
};
